import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { ShieldCheck } from 'lucide-react';
import { FC, useMemo } from 'react';
import moment from 'moment';
import classnames from 'classnames';
import useStore from '../../hooks/store/useStore';
import { REDUX_STORE_SLICES } from '../../services/exports/Constants';
import HighlightedText from '../inputs/HighlightedText';

interface Props {
  className?: string;
  contentClassName?: string;
  textClassname?: string;
  textHighlightClassname?: string;
  iconClassname?: string;
}

export enum TrustLabelPeriod {
  LESS_THAN_1_MONTH = '< 1 month',
  ONE_MONTH_OR_MORE = '>= 1 month',
  SIX_MONTHS_OR_MORE = '>=6 months',
}

const TrustLabel: FC<Props> = ({
  className,
  textClassname,
  iconClassname,
  contentClassName,
  textHighlightClassname,
}): JSX.Element => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Labels:TrustLabel' });
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);

  const period = useMemo(() => {
    if (!company?.live_from) {
      return null;
    }

    const liveFrom = moment(company.live_from);
    const now = moment();

    const diffInMonths = now.diff(liveFrom, 'months', true);

    if (diffInMonths >= 6) return TrustLabelPeriod.SIX_MONTHS_OR_MORE;

    if (diffInMonths >= 1) return TrustLabelPeriod.ONE_MONTH_OR_MORE;

    return TrustLabelPeriod.LESS_THAN_1_MONTH;
  }, [company?.live_from]);

  return (
    period && (
      <Card className={classnames('border-green-500/20 bg-green-500/5', className)}>
        <CardContent className={classnames('lg:py-3 px-sm py-sm flex items-center', contentClassName)}>
          <div className="flex h-fit items-center gap-2">
            <ShieldCheck className={classnames('h-5 w-5 text-green-500 flex-shrink-0', iconClassname)} />
            <p className={classnames('text-xs', textClassname)}>
              <HighlightedText
                text={t(`labels.${period}`)}
                highlight={t(`labels_highlight.${period}`)}
                className={classnames('font-bold', textHighlightClassname)}
              />
            </p>
          </div>
        </CardContent>
      </Card>
    )
  );
};

export default TrustLabel;
